<template>
  <Container>
    <div class="ding-toolbar">
      <div class="ding-toolbar-left">
        <Input search clearable style="width:200px;" v-model="filter.search" placeholder="搜索部门名称" />
        <span class="ding-muted">共 {{ flatDeps.length }} 个部门</span>
      </div>
      <div class="ding-toolbar-right">
        <Button icon="md-refresh" :loading="loading" @click="loadDeps(false)">刷新</Button>
        <Button icon="md-sync" :loading="loading" @click="loadDeps(true)">强制刷新缓存</Button>
      </div>
    </div>

    <div class="ding-split">
      <div class="ding-tree-pane">
        <Spin fix v-if="loading">
          <BaseLoading />
        </Spin>
        <BaseTree
          :data="treeData"
          :nodeRenderer="renderNode"
          style="min-width:220px;height:100%;overflow:auto;"
          @event="handleTreeEvent"
        />
      </div>

      <div class="ding-main-pane">
        <BaseEmpty v-if="!selected.id && !ipError.visible" msg="请选择左侧部门" />
        <div v-if="ipError.visible" class="ding-ip-box">
          <h3>通讯录接口受 IP 白名单限制</h3>
          <p>流程接口走 <code>api.dingtalk.com</code>，一般不校验出口 IP；组织架构走 <code>oapi.dingtalk.com</code>，会校验<strong>服务器出口 IP</strong>。</p>
          <p>请到钉钉开发者后台 → 应用 → <strong>开发管理 → 服务器出口 IP</strong>，填入后端服务器的公网出口 IP（不是你电脑浏览器的 IP）。</p>
          <p v-if="ipError.ip">钉钉返回未放行 IP：<code class="ding-ip-code">{{ ipError.ip }}</code>（请把这个加进白名单）</p>
          <pre class="ding-ip-raw">{{ ipError.message }}</pre>
          <div class="ding-toolbar-right" style="margin-top:12px;">
            <Button type="primary" :loading="loading" @click="loadDeps(true)">重试（强制刷新）</Button>
            <Button @click="ipError.visible = false">关闭</Button>
          </div>
        </div>
        <template v-else-if="selected.id">
          <div class="ding-dep-head">
            <div>
              <h3>
                <a class="ding-link" @click="openDeptDrawer(selected.id)">{{ selected.name }}</a>
              </h3>
              <div class="ding-muted">
                部门 ID：{{ selected.id }} · 上级：
                <a
                  v-if="selected.parent_id && selected.parent_id != 1"
                  class="ding-link"
                  @click="openDeptDrawer(selected.parent_id)"
                >{{ parentDeptName }}</a>
                <span v-else>{{ parentDeptName }}</span>
              </div>
            </div>
            <div class="ding-toolbar-right">
              <Input search clearable style="width:180px;" v-model="filter.userSearch" placeholder="搜索本部门员工" />
              <Button @click="openDeptDrawer(selected.id)">部门详情</Button>
              <Button :loading="loadingUsers" @click="loadDeptUsers">加载员工</Button>
            </div>
          </div>
          <div class="ding-table-inner">
            <BaseTable
              :columns="userColumns"
              :data="filteredUsers"
              :loading="loadingUsers"
              border
            />
          </div>
        </template>
      </div>
    </div>

    <DingUserDrawer
      v-model="userDrawer.visible"
      :userid="userDrawer.userid"
      @open-dept="openDeptDrawer"
      @open-user="openUserDrawer"
    />
    <DingDeptDrawer
      v-model="deptDrawer.visible"
      :dept-id="deptDrawer.deptId"
      @open-user="openUserDrawer"
      @open-dept="openDeptDrawer"
    />
  </Container>
</template>

<script>
import Container from '@/components/app/container.vue';
import {
  ensureDingDirectory,
  getDeptName,
} from './ding-directory';
import DingUserDrawer from './DingUserDrawer.vue';
import DingDeptDrawer from './DingDeptDrawer.vue';

export default {
  name: 'DingDeps',
  components: {Container, DingUserDrawer, DingDeptDrawer},
  data() {
    return {
      loading: false,
      loadingUsers: false,
      flatDeps: [],
      selected: {},
      users: [],
      filter: {
        search: '',
        userSearch: '',
      },
      userDrawer: {
        visible: false,
        userid: '',
      },
      deptDrawer: {
        visible: false,
        deptId: '',
      },
      ipError: {
        visible: false,
        message: '',
        ip: '',
      },
    };
  },
  computed: {
    /**
     * BaseTree 默认用 makeTree：父节点不在列表里时会丢弃子节点。
     * 钉钉一级部门 parent_id=1，但根部门 1 不在 listsub 结果中，需把「父不存在」的节点提成根。
     */
    treeData() {
      const q = (this.filter.search || '').trim().toLowerCase();
      let source = this.flatDeps || [];
      if (q) {
        const matched = new Set();
        source.forEach((d) => {
          if (String(d.name || '').toLowerCase().includes(q) || String(d.id).includes(q)) {
            matched.add(d.id);
          }
        });
        let changed = true;
        while (changed) {
          changed = false;
          source.forEach((d) => {
            if (matched.has(d.id) && d.parent_id != null && !matched.has(d.parent_id)) {
              // 继续向上找祖先（若祖先在全量列表中）
              const parent = this.flatDeps.find((x) => x.id == d.parent_id);
              if (parent) {
                matched.add(parent.id);
                changed = true;
              }
            }
          });
        }
        source = source.filter((d) => matched.has(d.id));
      }

      const idSet = new Set(source.map((d) => d.id));
      return source.map((d) => {
        const parentExists = d.parent_id != null && idSet.has(d.parent_id);
        // 父是钉钉根 1、或不在当前列表：作为树根显示
        if (!parentExists) {
          return Object.assign({}, d, {parent_id: null});
        }
        return Object.assign({}, d);
      });
    },
    parentDeptName() {
      if (!this.selected || this.selected.parent_id == null) return '-';
      return getDeptName(this.selected.parent_id) || String(this.selected.parent_id);
    },
    filteredUsers() {
      const q = (this.filter.userSearch || '').trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter((u) => {
        return String(u.name || '').toLowerCase().includes(q)
          || String(u.userid || '').toLowerCase().includes(q)
          || String(u.mobile || '').includes(q);
      });
    },
    userColumns() {
      return [{
        type: 'index',
        width: 60,
      }, {
        title: '姓名',
        key: 'name',
        minWidth: 100,
        render: (h, {row}) => h('a', {
          style: {color: 'var(--primary)', cursor: 'pointer'},
          on: {click: () => this.openUserDrawer(row.userid)},
        }, row.name || '-'),
      }, {
        title: 'userid',
        key: 'userid',
        minWidth: 140,
      }, {
        title: '手机号',
        key: 'mobile',
        width: 130,
      }, {
        title: '职位',
        key: 'title',
        minWidth: 120,
      }, {
        title: '工号',
        key: 'job_number',
        width: 100,
      }, {
        title: '操作',
        width: 90,
        render: (h, {row}) => h('Button', {
          props: {size: 'small', type: 'text'},
          on: {click: () => this.openUserDrawer(row.userid)},
        }, '详情'),
      }];
    },
  },
  mounted() {
    this.$api.afterLogin(() => this.loadDeps(false));
  },
  methods: {
    unwrap(res) {
      return (res && res.data && res.data.data) || (res && res.data) || {};
    },
    openUserDrawer(userid) {
      if (userid == null || userid === '') return;
      this.userDrawer.userid = String(userid);
      this.userDrawer.visible = true;
    },
    openDeptDrawer(deptId) {
      if (deptId == null || deptId === '') return;
      this.deptDrawer.deptId = deptId;
      this.deptDrawer.visible = true;
    },
    renderNode(h, node) {
      return h('span', node.name || String(node.id));
    },
    handleTreeEvent({type, data}) {
      if (type === 'select' && data) {
        this.selected = data;
        this.users = [];
        this.loadDeptUsers();
      }
    },
    extractErrorText(e) {
      if (!e) return '';
      if (typeof e === 'string') return e;
      const d = e.response && e.response.data;
      if (d) {
        if (typeof d === 'string') return d;
        return d.message || d.errmsg || (d.data && d.data.message) || e.message || JSON.stringify(d);
      }
      return e.message || String(e);
    },
    extractWhitelistIp(text) {
      const m = String(text || '').match(/(?:\b|ip\s*)((?:\d{1,3}\.){3}\d{1,3})/i);
      return m ? m[1] : '';
    },
    isIpWhitelistError(text) {
      return /白名单|whitelist|not in.*ip|ip.*not in/i.test(String(text || ''));
    },
    showApiError(e) {
      const text = this.extractErrorText(e);
      if (this.isIpWhitelistError(text)) {
        this.ipError = {
          visible: true,
          message: text,
          ip: this.extractWhitelistIp(text),
        };
        this.$Modal.error({
          title: 'IP 不在白名单',
          width: 640,
          content: [
            '<div style="line-height:1.7">',
            '<p>组织架构走 <b>oapi.dingtalk.com</b>，会校验<strong>服务器出口 IP</strong>；流程走 api.dingtalk.com，所以会出现「流程正常、组织报白名单」。</p>',
            this.ipError.ip ? `<p>请把此 IP 加到应用「开发管理 → 服务器出口 IP」：<code>${this.ipError.ip}</code></p>` : '<p>请打开钉钉返回原文，把其中的 IP 加到白名单。</p>',
            `<p style="color:#888;word-break:break-all;font-size:12px;">${text.replace(/</g, '&lt;')}</p>`,
            '</div>',
          ].join(''),
        });
        return;
      }
      this.Error(e);
    },
    async loadDeps(forced) {
      this.loading = true;
      this.ipError.visible = false;
      try {
        await ensureDingDirectory(this.$api, {forced});
        const qs = forced ? '?forced=1' : '';
        const res = await this.$api.get(`/ding/deps${qs}`);
        const data = this.unwrap(res);
        this.flatDeps = Array.isArray(data.list) ? data.list : [];
      } catch (e) {
        this.showApiError(e);
      } finally {
        this.loading = false;
      }
    },
    async loadDeptUsers() {
      if (!this.selected.id) return;
      this.loadingUsers = true;
      try {
        const res = await this.$api.get(`/ding/users?deptId=${encodeURIComponent(this.selected.id)}`);
        const data = this.unwrap(res);
        this.users = Array.isArray(data.list) ? data.list : [];
      } catch (e) {
        this.showApiError(e);
      } finally {
        this.loadingUsers = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.ding-toolbar {
  flex-shrink: 0;
  box-sizing: border-box;
  min-height: 50px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}
.ding-toolbar-left,
.ding-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.ding-muted {
  color: var(--text2);
  font-size: 12px;
}
.ding-split {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  display: flex;
  align-items: stretch;
  overflow: hidden;
}
.ding-tree-pane {
  width: 280px;
  flex-shrink: 0;
  border-right: 1px solid var(--border);
  position: relative;
  overflow: auto;
}
.ding-main-pane {
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ding-dep-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 10px;
  flex-wrap: wrap;
  h3 {
    margin: 0 0 4px;
    font-size: 16px;
  }
}
.ding-table-inner {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow: hidden;
  position: relative;
}
.ding-ip-box {
  padding: 20px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  h3 {
    margin: 0 0 12px;
    font-size: 16px;
  }
  p {
    margin: 0 0 10px;
    line-height: 1.6;
    color: var(--text);
    font-size: 13px;
  }
  code {
    padding: 1px 6px;
    background: var(--bg2);
    border-radius: 3px;
  }
}
.ding-ip-code {
  color: var(--error, #ed4014);
  font-weight: 600;
}
.ding-ip-raw {
  margin: 8px 0 0;
  padding: 10px;
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 160px;
  overflow: auto;
}
.ding-link {
  color: var(--primary);
  cursor: pointer;
}
</style>
